import type {
  LedgerAccountUniquenessResult,
  ValidateLedgerAccountUniquenessQueryParams,
} from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type KyRequestOptions, VestigasHTTPError } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { zodValidateLedgerAccountUniquenessQueryParamsSchema } from './validations';

/**
 * Checks whether a ledger account is unique for the caller's own company
 * account. The API signals the answer through the status code rather than a
 * response body: `200` means a duplicate exists, `404` means it is unique.
 *
 * @param queryParams - The number/company/valid-from combination to check.
 * @param options - Additional request options.
 * @returns Whether a matching version already exists.
 * @throws VestigasHTTPError for any failure other than the expected `404`.
 * @see https://app.dev.vestigas.com/redoc#tag/Ledger-Accounts/operation/validate_ledger_account_uniqueness_ledger_accounts_validate_uniqueness_get
 */
export const fetchLedgerAccountUniqueness = async (
  queryParams: ValidateLedgerAccountUniquenessQueryParams,
  options?: KyRequestOptions,
): Promise<LedgerAccountUniquenessResult> => {
  try {
    await vestigasApi.get(ENDPOINT.LEDGER_ACCOUNT.VALIDATE_UNIQUENESS(), {
      searchParams: queryParams,
      ...options,
      validate: {
        ...options?.validate,
        searchParams: zodValidateLedgerAccountUniquenessQueryParamsSchema,
      },
    });

    return {
      isDuplicate: true,
    };
  } catch (error) {
    if (error instanceof VestigasHTTPError && error.response.status === 404) {
      return {
        isDuplicate: false,
      };
    }

    throw error;
  }
};
